import React, { useState, useCallback } from 'react'
import { Box, Checkbox, Chip, FormControlLabel, IconButton, Tooltip } from '@mui/material'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import { apiPut, apiPost, apiDelete } from '../generic/Api_Functions'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useUser } from '../generic/hooks/useUser'
import { getRandomUUID } from '../generic/utilities/randomuuid'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { systemValueCodeLabel } from '../generic/systemValues/systemValueLabel'

// CA6: polling interval in seconds (SmartMateriaUITable fetchInterval prop)
const POLLING_INTERVAL_SECONDS = 5

// Status badge — read-only, never sent to API with user intent
const StatusBadge = ({ value, t_ }) => {
  const colorMap = {
    CORRECT: 'success',
    INCORRECT: 'error',
    UNKNOWN: 'warning',
    // D13: DOWN is the confirmed-removed terminal state of a deleted service — a neutral
    // informational badge, distinct from the INCORRECT failure red. Read-only like the rest.
    DOWN: 'info'
  }
  return (
    <Chip
      label={systemValueCodeLabel(t_, 'ProxiedServiceStatus', value || 'UNKNOWN')}
      color={colorMap[value] || 'default'}
      size='small'
      variant='outlined'
    />
  )
}

/**
 * Backoffice table for Proxied Services (F1 — SUPERADMIN only).
 * - CA6 polling: re-fetches while any service has status UNKNOWN.
 * - D5/P6/R7: lists active services by default, a "show deleted" toggle includes the logically
 *   deleted (inactive) ones, distinguishes active/inactive, and lets the user reactivate them.
 */
const ProxiedServices = () => {
  const { t_ } = useSmartTranslation()
  const { user } = useUser()
  const [showInactive, setShowInactive] = useState(false)
  const [reloadFlag, setReloadFlag] = useState(false)

  // D5/P6: active-only by default; when "show deleted" is on, include inactive (no active filter).
  const dataFetch = useCallback(async (requestPagination) => {
    const request = { ...requestPagination }
    if (!showInactive) {
      request.parameterFilters = [...(request.parameterFilters || []), 'active']
      request.parameterFiltersValues = [...(request.parameterFiltersValues || []), 'true']
      request.parameterFiltersNames = [...(request.parameterFiltersNames || []), 'active']
    }
    return await apiPut('backoffice/proxied-services/search', request)
  }, [showInactive])

  // Guard: only SUPERADMIN may access this component (D1/P5)
  if (user?.profile?.code !== 'SUPERADMIN') {
    return null
  }

  const triggerReload = () => setReloadFlag((flag) => !flag)

  // R7/D5: reactivation is the only way to reuse a globally-unique name/address/prefix
  const reactivate = (row) =>
    apiPost('backoffice/proxied-services/' + row.id + '/reactivate', {}).then(triggerReload)

  // CA6: keep polling while at least one service has status UNKNOWN
  const pollWhile = (rows) => rows.some((r) => r.status === 'UNKNOWN')

  const columns = [
    {
      name: t_('Name'),
      field: 'name',
      mandatory: true,
      charLimit: 255
    },
    {
      name: t_('Port'),
      field: 'port',
      numeric: true,
      mandatory: true
    },
    {
      name: t_('Address'),
      field: 'address',
      mandatory: true,
      charLimit: 255
    },
    {
      name: t_('Prefix'),
      field: 'prefix',
      mandatory: true,
      charLimit: 255
    },
    {
      name: t_('Status'),
      field: 'status',
      // R6/D10: status is read-only — rendered as badge, never editable
      render: (row) => <StatusBadge value={row.status} t_={t_} />,
      edit: false,
      sortable: true
    },
    {
      name: t_('Active'),
      field: 'active',
      edit: false,
      render: (row) => (
        <Chip
          label={row.active ? t_('Active') : t_('Inactive')}
          color={row.active ? 'success' : 'default'}
          size='small'
          variant='outlined'
        />
      )
    }
  ]

  const actions = {
    create: {
      // The frontend generates the UUID for a new proxied service (id sent on create)
      fetchFunction: (row) => apiPost('backoffice/proxied-services', { ...row, id: getRandomUUID() })
    },
    edit: {
      fetchFunction: (row) => apiPut('backoffice/proxied-services/' + row.id, row)
    },
    delete: {
      // Logical delete leaves no UNKNOWN row, so polling stays off — force a reload so the
      // deactivated service drops out of the (active-only) default view.
      fetchFunction: (rows) => {
        const request = rows.length === 1
          ? apiDelete('backoffice/proxied-services/' + rows[0].id)
          : apiDelete('backoffice/proxied-services', rows)
        return request.then((response) => {
          triggerReload()
          return response
        })
      }
    },
    custom: [
      {
        // R7: reactivate is shown inline only for inactive (logically deleted) rows
        contextMenu: false,
        render: (row) => (
          row.active
            ? null
            : (
              <Tooltip title={t_('Reactivate')}>
                <IconButton size='small' onClick={() => reactivate(row)}>
                  <SettingsBackupRestoreIcon fontSize='small' />
                </IconButton>
              </Tooltip>
              )
        )
      }
    ]
  }

  return (
    <>
      <Box sx={{ px: 1, pt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showInactive}
              onChange={(event) => {
                setShowInactive(event.target.checked)
                triggerReload()
              }}
            />
          }
          label={t_('Show deleted')}
        />
      </Box>
      <SmartMateriaUITable
        tableTuneUniqueKey='backoffice-proxied-services'
        columns={columns}
        title={t_('Proxied Services')}
        dataFetch={dataFetch}
        actions={actions}
        pageable
        multipleOptions
        multipleSelection
        fetchInterval={POLLING_INTERVAL_SECONDS}
        pollWhile={pollWhile}
        forceLoad={reloadFlag}
      />
    </>
  )
}

export default ProxiedServices
